import { events } from '@theme/event-bus';
import { HTMLCustomElement } from '@theme/html-custom-element';

class ProductOptionDropdown extends HTMLCustomElement {
  /** @type {ResizeObserver | undefined} */
  listAlignObserver;

  connectedCallback() {
    if (super.connectedCallback) super.connectedCallback();

    this.syncListAlign = this.syncListAlign.bind(this);

    this.on('click', this.refs.trigger, (event) => {
      event.stopPropagation();
      this.toggle();
    });
    this.on('click', this.refs.option && this.refs.option.all, this.selectOption.bind(this));
    this.on('click', document, this.onDocumentClick.bind(this));
    this.on('resize', window, this.syncListAlign);
    this.subscribe(events.escapeKey, this.onEscape.bind(this));

    requestAnimationFrame(() => this.syncListAlign());

    if (typeof ResizeObserver !== 'undefined') {
      this.listAlignObserver = new ResizeObserver(() => this.syncListAlign());
      this.listAlignObserver.observe(this);
      if (this.refs.trigger) {
        this.listAlignObserver.observe(this.refs.trigger);
      }
      if (this.refs.current) {
        this.listAlignObserver.observe(this.refs.current);
      }
    }
  }

  disconnectedCallback() {
    this.listAlignObserver?.disconnect();
    this.listAlignObserver = undefined;
    if (super.disconnectedCallback) super.disconnectedCallback();
  }

  /**
   * Aligns list rows with the selected-value (.current) by matching its inset from the host left edge.
   */
  syncListAlign() {
    if (!this.refs.current) return;

    const hostRect = this.getBoundingClientRect();
    const currentRect = this.refs.current.getBoundingClientRect();
    const inset = Math.round(currentRect.left - hostRect.left);

    this.style.setProperty('--product-option-list-inset', `${Math.max(0, inset)}px`);
  }

  onEscape() {
    if (this.isOpen) this.close();
  }

  onDocumentClick(event) {
    if (!this.isOpen) return;
    if (!this.contains(event.target)) this.close();
  }

  get isOpen() {
    return this.classList.contains('is-open');
  }

  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    if (!this.refs.trigger || !this.refs.panel) return;
    this.classList.add('is-open');
    this.refs.trigger.setAttribute('aria-expanded', 'true');
    this.refs.panel.hidden = false;
    requestAnimationFrame(() => this.syncListAlign());
  }

  close() {
    if (!this.refs.trigger || !this.refs.panel) return;
    this.classList.remove('is-open');
    this.refs.trigger.setAttribute('aria-expanded', 'false');
    this.refs.panel.hidden = true;
  }

  /**
   * @param {MouseEvent} event
   */
  selectOption(event) {
    const button = event.currentTarget;
    if (!(button instanceof HTMLButtonElement)) return;

    const { inputId } = button.dataset;
    if (!inputId) return;

    const input = document.getElementById(inputId);
    if (!input || input.disabled) return;

    if (input.checked) {
      this.close();
      return;
    }

    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));

    const main = button.querySelector('.product-option-dropdown__option-main');
    if (main && this.refs.current) {
      this.refs.current.innerHTML = main.innerHTML;
    }

    this.close();
    requestAnimationFrame(() => this.syncListAlign());
  }
}

if (!customElements.get('product-option-dropdown')) {
  customElements.define('product-option-dropdown', ProductOptionDropdown);
}
